import { buildVipCycles, buildDirectVipCycles, summarizeVipCycles } from "./vipUsageCycles";
import { formatReportDate, validateFrequencyFilters } from "./frequencyModel";
import { validateVipUsageFilters } from "./vipUsageDates";

export const normalizePlate = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();
const dateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Bogota",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const timeFormatter = new Intl.DateTimeFormat("es-CO", {
  timeZone: "America/Bogota",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});
export function vipTimestamp(value) {
  if (typeof value !== "string" || /^2000-01-01/.test(value)) return null;
  const normalized = value
    .trim()
    .replace(" ", "T")
    .replace(/([+-]\d{2})$/, "$1:00")
    .replace(/\.(\d{3})\d+/, ".$1");
  const match = normalized.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i
  );
  if (
    !match ||
    validateFrequencyFilters({ startDate: match[1], endDate: match[1] }) ||
    +match[2] > 23 ||
    +match[3] > 59 ||
    +match[4] > 59
  )
    return null;
  const ms = Date.parse(normalized);
  if (!Number.isFinite(ms)) return null;
  const parts = Object.fromEntries(
    dateFormatter.formatToParts(new Date(ms)).map((p) => [p.type, p.value])
  );
  return {
    ms,
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: timeFormatter.format(new Date(ms)),
  };
}
export const vehicleTypeFromPlate = (plate) =>
  /^[A-Z]{3}\d{2}[A-Z]$/.test(normalizePlate(plate))
    ? "Moto"
    : /^[A-Z]{3}\d{3}$/.test(normalizePlate(plate))
    ? "Carro"
    : null;
export const VIP_USAGE_COLUMNS = [
  ["plate", "Placa"],
  ["vehicleType", "Tipo de vehículo"],
  ["groupName", "Grupo"],
  ["entryDate", "Fecha de ingreso"],
  ["entryTerminal", "Terminal de ingreso"],
  ["entryTime", "Hora de ingreso general"],
  ["vipEntryTime", "Hora de ingreso VIP"],
  ["entryMinutes", "Duración in-Gen/in-VIP (min)"],
  ["exitDate", "Fecha de salida"],
  ["vipExitTime", "Hora de salida VIP"],
  ["exitTime", "Hora de salida general"],
  ["exitTerminal", "Terminal de salida"],
  ["exitMinutes", "Duración Out-VIP/Out-Gen (min)"],
  ["totalMinutes", "Duración total (min)"],
];
export const VIP_HISTORY_COLUMNS = [
  ["plate", "Placa"],
  ["vehicleType", "Tipo de vehículo"],
  ["groupName", "Grupo actual"],
  ["entryDate", "Fecha de ingreso general"],
  ["entryTime", "Ingreso general"],
  ["vipEntryAt", "Ingreso VIP"],
  ["vipExitAt", "Salida VIP"],
  ["exitDate", "Fecha de salida general"],
  ["exitTime", "Salida general"],
  ["vipMinutes", "Permanencia VIP (min)"],
  ["totalMinutes", "Permanencia total (min)"],
  ["pairingStatus", "Estado del ciclo"],
  ["eventIds", "IDs de movimientos"],
];

export const VIP_EVENT_COLUMNS = [
  ["plate", "Placa"],
  ["vehicleType", "Tipo de vehículo"],
  ["groupName", "Grupo actual"],
  ["vipEntryAt", "Ingreso VIP"],
  ["vipExitAt", "Salida VIP"],
  ["entryTerminal", "Lector de ingreso VIP"],
  ["exitTerminal", "Lector de salida VIP"],
  ["vipMinutes", "Permanencia VIP (min)"],
  ["pairingStatus", "Estado"],
  ["capture", "TAG"],
];
export const vipHistoryColumns = (report) =>
  report?.source === "vip-events" ? VIP_EVENT_COLUMNS : VIP_HISTORY_COLUMNS;

// Each entry receives the next unused exit for the same plate, in chronological order.
export function pairVipHistory(events) {
  const ordered = [...events].sort(
    (a, b) =>
      a.ms - b.ms ||
      (a.eventType === b.eventType
        ? a.eventId.localeCompare(b.eventId, "en", { numeric: true })
        : a.eventType === "GPIN"
        ? -1
        : 1)
  );
  const queues = new Map();
  const rows = [];
  for (const event of ordered) {
    const plate = normalizePlate(event.plate);
    if (event.eventType === "GPIN") {
      const row = {
        ...event,
        entryEventId: event.eventId,
        exitEventId: null,
        entryDate: event.date,
        entryTime: event.time,
        exitDate: null,
        exitTime: null,
        totalMinutes: null,
        pairingStatus: "Sin salida",
      };
      rows.push(row);
      if (plate) {
        if (!queues.has(plate)) queues.set(plate, { rows: [], next: 0 });
        queues.get(plate).rows.push(row);
      }
    } else {
      const queue = plate ? queues.get(plate) : null;
      const entry = queue?.rows[queue.next];
      if (entry) {
        queue.next += 1;
        entry.exitEventId = event.eventId;
        entry.exitDate = event.date;
        entry.exitTime = event.time;
        entry.totalMinutes = (event.ms - entry.ms) / 60000;
        entry.pairingStatus = "Entrada y salida";
      } else {
        rows.push({
          ...event,
          entryEventId: null,
          exitEventId: event.eventId,
          entryDate: null,
          entryTime: null,
          exitDate: event.date,
          exitTime: event.time,
          totalMinutes: null,
          pairingStatus: "Sin entrada",
        });
      }
    }
  }
  return rows.sort((a, b) => b.ms - a.ms || a.plate.localeCompare(b.plate));
}
export const usageCell = (row, key) =>
  ["vipEntryAt", "vipExitAt"].includes(key)
    ? row[key === "vipEntryAt" ? "vipEntryDate" : "vipExitDate"]
      ? `${formatReportDate(row[key === "vipEntryAt" ? "vipEntryDate" : "vipExitDate"])} ${
          row[key === "vipEntryAt" ? "vipEntryTime" : "vipExitTime"]
        }`
      : "Sin información"
    : ["entryDate", "exitDate", "date"].includes(key) && row[key]
    ? formatReportDate(row[key])
    : row[key] == null || row[key] === ""
    ? "Sin información"
    : typeof row[key] === "number"
    ? Number(row[key].toFixed(2)).toLocaleString("es-CO", { maximumFractionDigits: 2 })
    : row[key];
const inRange = (date, filters) => date >= filters.startDate && date <= filters.endDate;
const matches = (row, filters) =>
  (!filters.plate || normalizePlate(row.plate).includes(normalizePlate(filters.plate))) &&
  (!filters.groupId || String(row.groupId) === String(filters.groupId));
const minutes = (start, end) =>
  start && end && end.ms >= start.ms ? (end.ms - start.ms) / 60000 : null;

export function buildVipUsageReport(
  {
    groups = [],
    members = [],
    details = [],
    times = [],
    events = [],
    visits = [],
    source = "snapshot",
    warnings: initialWarnings = [],
  },
  filters
) {
  const invalid = validateVipUsageFilters(filters);
  if (invalid) throw new Error(invalid);
  const warnings = [...initialWarnings];
  const groupsMap = new Map(groups.map((g) => [String(g.idgroup), g]));
  const memberMap = new Map();
  for (const member of members) {
    const key = normalizePlate(member.plate);
    if (!key) continue;
    const previous = memberMap.get(key);
    if (
      previous &&
      (String(previous.idvehicle) !== String(member.idvehicle) ||
        String(previous.idgroup) !== String(member.idgroup))
    )
      memberMap.set(key, { ambiguous: true });
    else if (!previous?.ambiguous) memberMap.set(key, member);
  }
  const detailMap = new Map(details.map((d) => [String(d.idvehicle), d]));
  const metadata = (record) => {
    const plate = normalizePlate(
      record.plate || record.placa || record.placa_capturada || record.bike_code
    );
    const member = memberMap.get(plate);
    const detail = member && !member.ambiguous ? detailMap.get(String(member.idvehicle)) : null;
    const groupId =
      source === "visits" ? record.idgroup : member?.ambiguous ? null : member?.idgroup;
    const type = record.vehicle_type || detail?.vehicle_type || vehicleTypeFromPlate(plate);
    const typeId = record.idvehicletype ?? detail?.idvehicletype;
    return {
      plate,
      groupId,
      groupName:
        record.group_name ||
        groupsMap.get(String(groupId))?.name ||
        (!member?.ambiguous && member?.name) ||
        null,
      vehicleType: type || (typeId != null ? `Tipo ${typeId} (por confirmar)` : null),
    };
  };
  if (source === "snapshot")
    warnings.push(
      "Vista preliminar: los tiempos actuales contienen solo el último registro por placa; no representan todas las visitas del rango. Los grupos son los asignados actualmente."
    );
  let invalidTimes = 0,
    outsideEvents = 0,
    unknownTypes = 0,
    missingTerminals = 0,
    unverifiedHistory = 0;
  const seen = new Set();
  const allRows = [];
  for (const record of source === "visits" ? visits : times) {
    const entry = vipTimestamp(record.ent_gen);
    if (!entry || !inRange(entry.date, filters)) continue;
    const meta = metadata(record);
    if (!matches(meta, filters)) continue;
    if (source === "visits" && (record.id == null || String(record.id).trim() === ""))
      throw new Error("Cada visita histórica VIP necesita un identificador único.");
    const id = source === "visits" ? String(record.id) : `${meta.plate}:${entry.ms}`;
    if (seen.has(id)) continue;
    seen.add(id);
    let exit = vipTimestamp(record.sal_gen),
      vipEntry = vipTimestamp(record.ent_vip),
      vipExit = vipTimestamp(record.sal_vip);
    if (exit && exit.ms < entry.ms) {
      exit = null;
      invalidTimes++;
    }
    if (vipEntry && (vipEntry.ms < entry.ms || (exit && vipEntry.ms > exit.ms))) {
      vipEntry = null;
      invalidTimes++;
    }
    if (vipExit && (vipExit.ms < (vipEntry || entry).ms || (exit && vipExit.ms > exit.ms))) {
      vipExit = null;
      invalidTimes++;
    }
    if (!meta.vehicleType || meta.vehicleType.startsWith("Tipo ")) unknownTypes++;
    if (!record.entry_terminal || !record.exit_terminal) missingTerminals++;
    allRows.push({
      ...meta,
      id,
      entryDate: entry.date,
      entryTime: entry.time,
      vipEntryTime: vipEntry?.time,
      vipEntryDate: vipEntry?.date,
      vipExitDate: vipExit?.date,
      vipEntryAt: vipEntry ? `${vipEntry.date}T${vipEntry.time}` : null,
      vipExitAt: vipExit ? `${vipExit.date}T${vipExit.time}` : null,
      isVipVisit: Boolean(vipEntry),
      vipMinutes: minutes(vipEntry, vipExit),
      entryTerminal: record.entry_terminal || null,
      exitDate: exit?.date,
      exitTime: exit?.time,
      vipExitTime: vipExit?.time,
      exitTerminal: record.exit_terminal || null,
      entryMinutes: minutes(entry, vipEntry),
      exitMinutes: minutes(vipExit, exit),
      totalMinutes: minutes(entry, exit),
    });
  }
  const rows = allRows
    .filter(
      (row) =>
        (!filters.entryOver15 || (row.entryMinutes != null && row.entryMinutes > 15)) &&
        (!filters.exitOver15 || (row.exitMinutes != null && row.exitMinutes > 15))
    )
    .sort(
      (a, b) =>
        b.entryDate.localeCompare(a.entryDate) ||
        b.entryTime.localeCompare(a.entryTime) ||
        a.plate.localeCompare(b.plate)
    );
  const seenEvents = new Set();
  const history = [];
  for (const event of events) {
    if (event.eventtype !== "GPIN" && event.eventtype !== "GPOUT") continue;
    const stamp = vipTimestamp(source === "vip-events" ? event.capturetime : event.fecha_captura);
    if (!stamp) {
      invalidTimes++;
      continue;
    }
    if (!inRange(stamp.date, filters)) {
      outsideEvents++;
      continue;
    }
    const eventId =
      source === "vip-events"
        ? JSON.stringify([
            normalizePlate(event.placa),
            event.capture,
            event.idreader,
            event.eventtype,
            stamp.ms,
          ])
        : event.idhistoryevent;
    if (eventId == null) throw new Error("Un movimiento no incluye idhistoryevent.");
    if (seenEvents.has(String(eventId))) continue;
    seenEvents.add(String(eventId));
    const meta = metadata(event);
    if (!matches(meta, filters)) continue;
    if (!meta.groupId) unverifiedHistory++;
    history.push({
      ...meta,
      eventId: String(eventId),
      eventType: event.eventtype,
      eventLabel: event.eventtype === "GPIN" ? "Entrada (GPIN)" : "Salida (GPOUT)",
      date: stamp.date,
      time: stamp.time,
      ms: stamp.ms,
      ...(source === "vip-events"
        ? {
            capture: event.capture || null,
            readerId: event.idreader ?? null,
            readerLabel: event.descripcion_lector
              ? `${event.descripcion_lector}${event.idreader != null ? ` (${event.idreader})` : ""}`
              : event.idreader != null
              ? `Lector ${event.idreader}`
              : null,
          }
        : {}),
    });
  }
  history.sort((a, b) => b.ms - a.ms);
  if (outsideEvents)
    warnings.push(
      `${outsideEvents} eventos llegaron fuera del rango solicitado y fueron excluidos. Revisar el filtro de fechas del backend.`
    );
  if (invalidTimes)
    warnings.push(
      `${invalidTimes} fechas o secuencias inconsistentes se excluyeron de los cálculos; no se mezclan ciclos diferentes.`
    );
  if (unknownTypes)
    warnings.push(
      "Hay vehículos cuyo tipo no puede identificarse con el formato de placa acordado; falta el catálogo de tipos."
    );
  if (missingTerminals)
    warnings.push("Los datos actuales no identifican todos los terminales de ingreso y salida.");
  if (unverifiedHistory)
    warnings.push(
      `${unverifiedHistory} movimientos no pudieron relacionarse con un grupo VIP actual.`
    );
  if (source === "snapshot" && (filters.entryOver15 || filters.exitOver15))
    warnings.push(
      "Los filtros de más de 15 minutos se aplican a los últimos tiempos; el historial bruto no identifica zonas y no admite esos filtros."
    );
  const cycles =
    source === "visits"
      ? rows.map((row) => ({
          ...row,
          eventIds: row.id,
          pairingStatus: !row.isVipVisit
            ? "Sin ingreso VIP"
            : row.vipMinutes == null
            ? "Sin salida VIP"
            : row.exitDate
            ? "Ciclo VIP completo"
            : "Sin salida general",
        }))
      : source === "vip-events"
      ? buildDirectVipCycles(history)
      : buildVipCycles(history);
  return {
    rows: source === "vip-events" ? cycles : rows,
    history: cycles,
    vipSummary: initialWarnings.some((warning) =>
      warning.includes("No se pudo consultar el historial")
    )
      ? null
      : summarizeVipCycles(cycles),
    historyEvents: history,
    groups,
    warnings: [...new Set(warnings)],
    source,
    filters: { ...filters },
    totalVisits:
      source === "vip-events" ? cycles.filter((row) => row.isVipVisit).length : rows.length,
    totalDays: new Set(
      source === "vip-events"
        ? cycles.filter((row) => row.isVipVisit).map((row) => row.vipEntryDate)
        : rows.map((r) => r.entryDate)
    ).size,
    entryOver15: rows.filter((r) => r.entryMinutes > 15).length,
    exitOver15: rows.filter((r) => r.exitMinutes > 15).length,
  };
}
